import React, { useState } from 'react';
import { FiX, FiUser, FiCalendar, FiUpload } from 'react-icons/fi';

export interface NewApontamentoData {
  tipo: string;
  apontamento: string;
  arquivo?: File;
}

interface NewApontamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: NewApontamentoData) => void;
  userName?: string;
}

export const NewApontamentoModal: React.FC<NewApontamentoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  userName = 'Rodrigo Camargo da Silva',
}) => {
  const [tipo, setTipo] = useState('');
  const [apontamento, setApontamento] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!tipo || !apontamento) {
      return;
    }

    onSave({
      tipo,
      apontamento,
      arquivo: arquivo || undefined,
    });

    // Reset form
    setTipo('');
    setApontamento('');
    setArquivo(null);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setArquivo(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setArquivo(e.target.files[0]);
    }
  };

  const currentDate = new Date().toLocaleDateString('pt-BR');

  const tipoOptions = [
    'Tipo de apontamento',
    'Análise pendente',
    'Documentação incompleta',
    'Informação divergente',
    'Outro',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h2 className="text-xl font-semibold text-neutral-800">Apontamento</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* User and Date */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <FiUser className="w-4 h-4 text-purple-600" />
              <span>{userName}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <FiCalendar className="w-4 h-4 text-purple-600" />
              <span>{currentDate}</span>
            </div>
          </div>

          {/* Tipo de apontamento */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Tipo de apontamento <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={tipo}
                onChange={(e) => { setTipo(e.target.value); }}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none text-sm bg-white"
              >
                <option value="">Selecionar</option>
                {tipoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Apontamento */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Apontamento <span className="text-red-500">*</span>
            </label>
            <textarea
              value={apontamento}
              onChange={(e) => { setApontamento(e.target.value); }}
              placeholder="Inserir"
              rows={4}
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
            />
          </div>

          {/* Anexar arquivo */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Anexar arquivo
            </label>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-neutral-300 bg-neutral-50'
              }`}
            >
              <input
                type="file"
                id="file-upload"
                onChange={handleFileChange}
                accept=".png,.jpg,.jpeg,.pdf"
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FiUpload className="w-8 h-8 text-neutral-400 mb-2" />
                <p className="text-sm text-neutral-600 mb-1">
                  Carregar um arquivo ou arraste e solte.
                </p>
                <p className="text-xs text-neutral-400">
                  PNG, JPG, PDF até 10MB
                </p>
                {arquivo && (
                  <p className="text-sm text-purple-600 mt-2 font-medium">
                    {arquivo.name}
                  </p>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-white border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!tipo || !apontamento}
            className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Salvar apontamento
          </button>
        </div>
      </div>
    </div>
  );
};
